import React, { useState } from 'react';
import { Send, ChevronDown, Hourglass } from 'lucide-react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';

export const appliedAt = (leave) => leave.applied_at || leave.created_at;

// Backend stores naive UTC ISO strings — treat them as UTC.
export const parseUtc = (iso) => (iso && !/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? new Date(`${iso}Z`) : new Date(iso));

export const waitingDays = (leave) => {
  const t = parseUtc(appliedAt(leave)).getTime();
  return Number.isNaN(t) ? 0 : Math.max(0, Math.floor((Date.now() - t) / 86400000));
};

export const WaitingChip = ({ leave }) => {
  const d = waitingDays(leave);
  const tone = d > 7 ? 'bg-red-100 text-red-700 border-red-200' : d > 3 ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-gray-100 text-gray-600 border-gray-200';
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${tone}`} data-testid={`waiting-chip-${leave.id}`}>
      <Hourglass className="h-3 w-3" />
      {d === 0 ? 'Waiting since today' : `Waiting ${d} day${d > 1 ? 's' : ''}`}
    </span>
  );
};

export const ResendApprovalMenu = ({ leave, managers, onDone }) => {
  const [busy, setBusy] = useState(false);
  const resend = async (managerId = '') => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/leaves/${leave.id}/resend-approval`, { manager_id: managerId });
      const lines = [
        data.sent_to.length ? `Sent to ${data.sent_to.join(', ')}` : '',
        ...data.failed.map((f) => `Failed: ${f.to} — ${f.error || 'unknown error'}`),
      ].filter(Boolean);
      toast({
        title: data.failed.length ? (data.sent_to.length ? 'Reminder partly sent' : 'Email failed') : data.sent_to.length ? 'Reminder sent' : 'Nothing sent',
        description: lines.join(' · ') || 'No manager email on file.',
        variant: data.failed.length || !data.sent_to.length ? 'destructive' : undefined,
      });
      onDone?.();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed to resend', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs text-indigo-700 border-indigo-200" disabled={busy} data-testid={`resend-approval-${leave.id}`}>
          <Send className="h-3 w-3 mr-1" />{busy ? 'Sending…' : 'Resend'}<ChevronDown className="h-3 w-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="text-xs">Send reminder to</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => resend()} data-testid={`resend-all-${leave.id}`}>
          {managers.length > 1 ? 'All reporting managers' : managers.length ? `${managers[0].name}` : 'HR (no manager on file)'}
        </DropdownMenuItem>
        {managers.length > 1 && <DropdownMenuSeparator />}
        {managers.length > 1 && managers.map((m) => (
          <DropdownMenuItem key={m.id} onClick={() => resend(m.id)} disabled={!m.email} data-testid={`resend-to-${leave.id}-${m.id}`}>
            {m.name}{m.email ? '' : ' (no email)'}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
